import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { AuthForms } from '~/components/landing/AuthForms';
import { LandingView } from '~/components/landing/LandingView';
import { useAnalytics } from '~/hooks/useAnalytics';
import { isAuthenticated } from '~/lib/auth';
import type { PublicLandingPage } from '~/types';

export const Route = createFileRoute('/$slug/auth')({
  component: AuthPage,
});

function AuthPage() {
  const router = useRouter();
  const { slug, landingPage } = Route.useRouteContext() as {
    slug: string;
    landingPage: PublicLandingPage;
  };
  const { trackEvent } = useAnalytics({ slug, landingPageId: landingPage.id });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Handle social auth error redirects
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error === 'account_exists') {
      setErrorMessage('An account with this email already exists. Please sign in with your email and password.');
    } else if (error === 'invalid_state' || error === 'auth_failed') {
      setErrorMessage('Authentication failed. Please try again.');
    }
    // Clean up the URL
    if (error) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  // If already authenticated, redirect immediately
  useEffect(() => {
    if (isAuthenticated()) {
      navigateAfterAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateAfterAuth = useCallback(() => {
    if (landingPage.targetType === 'show') {
      router.navigate({ to: '/$slug/episodes', params: { slug } });
    } else {
      router.navigate({
        to: '/$slug/game/$episodeId',
        params: { slug, episodeId: landingPage.targetId },
      });
    }
  }, [router, slug, landingPage]);

  const handleAuthSuccess = useCallback(
    (mode?: 'register' | 'login') => {
      if (mode === 'register') {
        trackEvent('registration_complete');
      } else if (mode === 'login') {
        trackEvent('login_complete');
      }
      navigateAfterAuth();
    },
    [trackEvent, navigateAfterAuth],
  );

  return (
    <LandingView landingPage={landingPage}>
      {errorMessage && (
        <div className="w-full max-w-sm mx-auto mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
          {errorMessage}
        </div>
      )}
      <AuthForms slug={slug} onSuccess={handleAuthSuccess} />
    </LandingView>
  );
}
